import { Box, Text, Textarea, TextareaProps } from '@chakra-ui/react'
import React, { useState } from 'react'

import { getNodeInputFieldStyles } from './nodeInputStyles'
import { NodeLabel } from './NodeLabel'

export interface NodeTextareaProps extends Omit<TextareaProps, 'size'> {
  label: string
  required?: boolean
  error?: string
  maxLength?: number
  showCharCount?: boolean
  onMouseDown?: (e: React.MouseEvent<HTMLTextAreaElement>) => void
  onClick?: (e: React.MouseEvent<HTMLTextAreaElement>) => void
}

/**
 * Standardized textarea component for switchboard nodes with floating label
 * Label appears on the border to save space
 * Automatically applies the 'nodrag' class to prevent React Flow drag events.
 */
export const NodeTextarea: React.FC<NodeTextareaProps> = ({
  label,
  required = false,
  error,
  maxLength,
  showCharCount = false,
  onMouseDown,
  onClick,
  placeholder,
  ...textareaProps
}) => {
  // Track if field has been focused to stop pulsing animation
  const [hasBeenFocused, setHasBeenFocused] = useState(false)

  // Reset hasBeenFocused when error clears
  React.useEffect(() => {
    if (!error) {
      setHasBeenFocused(false)
    }
  }, [error])

  const handleMouseDown = (e: React.MouseEvent<HTMLTextAreaElement>) => {
    onMouseDown?.(e)
    e.stopPropagation()
  }

  const handleClick = (e: React.MouseEvent<HTMLTextAreaElement>) => {
    onClick?.(e)
    e.stopPropagation()
    // Stop pulsing when clicked
    if (error && !hasBeenFocused) {
      setHasBeenFocused(true)
    }
  }

  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    textareaProps.onFocus?.(e)
    // Stop pulsing when focused
    if (error && !hasBeenFocused) {
      setHasBeenFocused(true)
    }
  }

  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    textareaProps.onBlur?.(e)
  }

  const showLabel = true // Always show labels

  const currentValue = String(textareaProps.value || '')
  const charCount = currentValue.length

  // Add pulsing animation for error state (until focused)
  const shouldPulse = error && !hasBeenFocused

  return (
    <Box position="relative" width="100%">
      <Textarea
        className="nodrag"
        {...textareaProps}
        {...getNodeInputFieldStyles(!!error)}
        placeholder={placeholder || label}
        maxLength={maxLength}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
        onFocus={handleFocus}
        onBlur={handleBlur}
        css={
          shouldPulse
            ? {
                '@keyframes pulse-border': {
                  '0%, 100%': {
                    borderColor: 'var(--chakra-colors-border-error)',
                    boxShadow:
                      '0 0 0 0 color-mix(in srgb, var(--chakra-colors-fg-error) 40%, transparent)',
                  },
                  '50%': {
                    borderColor: 'var(--chakra-colors-border-error)',
                    boxShadow:
                      '0 0 0 3px color-mix(in srgb, var(--chakra-colors-fg-error) 20%, transparent)',
                  },
                },
                animation: 'pulse-border 2s ease-in-out infinite',
              }
            : undefined
        }
      />
      {showLabel && <NodeLabel label={label} required={required} />}
      {(error || (showCharCount && maxLength)) && (
        <Box
          mt={1}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          {error && (
            <Text fontSize="xs" color="fg.error">
              {error}
            </Text>
          )}
          {showCharCount && maxLength && (
            <Text
              fontSize="xs"
              color={
                charCount > maxLength
                  ? 'fg.error'
                  : charCount > maxLength * 0.9
                    ? 'fg.warning'
                    : 'fg.muted'
              }
              ml={error ? 2 : 0}
            >
              {charCount}/{maxLength}
            </Text>
          )}
        </Box>
      )}
    </Box>
  )
}
